import React from "react"
import { useStaticQuery, graphql, Link } from "gatsby"
import Layout from "../components/layout"
import SEO from "../components/seo"
import PropTypes from "prop-types"

import PaperEntry from "../components/paper-entry"
import EventTag from "../components/event-tag"
import EventFields from "../components/event-fields"
import "./index.scss"

// Function to parse Markdown links and convert them to HTML with inline style
const parseMarkdownLinks = (text) => {
  if (!text) return text
  
  // Simple regex to match Markdown links: [text](url)
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
  const parts = []
  let lastIndex = 0
  let match
  
  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    
    // Add the link with inline style
    parts.push(
      `<a href="${match[2]}" target="_blank" rel="noopener noreferrer" style="color:#000;text-decoration:underline;">${match[1]}</a>`
    )
    
    lastIndex = match.index + match[0].length
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }
  
  return parts.join('')
}

const MoreCard = ({ link }) => (
  <Link className="undecorated-link" to={link}>
    <div className="index__card more">More&hellip;</div>
  </Link>
)

MoreCard.propTypes = {
  link: PropTypes.string.isRequired,
}

const IndexPage = () => {
  const { papers, events } = useStaticQuery(graphql`
    query HomePageQuery {
      papers: allMarkdownRemark(
        sort: {
          fields: [frontmatter___year, frontmatter___uploadMonth, frontmatter___title]
          order: DESC
        }
        filter: {
          fields: { sourceInstanceName: { eq: "publications" } }
          frontmatter: { feature: { eq: true } }
        }
        limit: 5
      ) {
        edges {
          node {
            id
            fields {
              slug
            }
            frontmatter {
              title
              publication
              authors
              thumbnail {
                childImageSharp {
                  fluid {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
              year
            }
          }
        }
      }
      events: allMarkdownRemark(
        sort: { fields: [frontmatter___startDate], order: DESC }
        filter: { fields: { sourceInstanceName: { eq: "events" } } }
        limit: 5
      ) {
        edges {
          node {
            id
            frontmatter {
              type
              title
              location
              startDate
              endDate
            }
          }
        }
      }
    }
  `)
  return (
    <Layout>
      <SEO title="Home" />
      <h2>About Us</h2>
      <p>
        Intelligent Data Exploration System (IDEAS) Lab (formerly known as "VisLab") was founded
        2016 at Shandong University, Qingdao, China by{" "}
        <a href="http://www.yunhaiwang.net">Prof. Yunhai Wang</a>. Its mission is to enhance
        people&apos;s ability to understand and communicate data through the design of automated
        visualization and visual analytics systems.
      </p>
      <p>
        Recently, we relocated to Renmin University of China and are in the process of building a
        new group. We are currently recruiting faculty members. For more information and to join our
        team, please visit{" "}
        <a href="http://www.yunhaiwang.net/recruit-faculty-en.html">
          http://www.yunhaiwang.net/recruit-faculty-en.html
        </a>
        .
      </p>
      <p>
        We study the perceptual and mathematical foundations of visualization in order to improve
        the efficiency of interactive data analysis, while developing interactive systems for data
        visualization and analysis.
      </p>
      <div className="index__columns">
        <div>
          <h2>Recent Publications</h2>
          <div className="index__card-list">
            {papers.edges.map(({ node: { id: paperId, fields: { slug }, frontmatter } }) => (
              <PaperEntry key={paperId} paper={{ ...frontmatter, slug }} showAuthors={false} />
            ))}
            <MoreCard link="/publications" />
          </div>
        </div>
        <div>
          <h2>Recent Events</h2>
          <div className="index__card-list">
            {events.edges.map(({ node: { id: eventId, frontmatter: event } }) => {
              // 判断标题中是否有外链
              const hasExternalLink = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/.test(event.title)
              return (
                <Link
                  className="index__card"
                  key={eventId}
                  to={`/events#${eventId}`}
                  style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="index__event-title" onClick={e => {
                    // 阻止点击标题时触发 Link 跳转
                    if (e.target.tagName === 'A') e.stopPropagation()
                  }}>
                    <EventTag type={event.type}>{event.type}</EventTag>
                    {/* 只让标题是外链 */}
                    <span dangerouslySetInnerHTML={{ __html: parseMarkdownLinks(event.title) }} />
                  </div>
                  <EventFields event={event} />
                </Link>
              )
            })}
            <MoreCard link="/events" />
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default IndexPage
